import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RhythmContext = createContext(null);

function getCycleDates(rhythmType, payAnchorType, payAnchorValue) {
  const now = new Date();
  let start, end;

  if (rhythmType === "weekly") {
    start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (rhythmType === "fortnightly") {
    // Anchor to the start of the year, find the current 2-week period
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const daysSinceYear = Math.floor((now - yearStart) / 86400000);
    const cycleIndex = Math.floor(daysSinceYear / 14);
    start = new Date(yearStart);
    start.setDate(yearStart.getDate() + cycleIndex * 14);
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(start.getDate() + 13);
  } else if (rhythmType === "monthly") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (rhythmType === "per_pay_cycle") {
    if (payAnchorType === "date") {
      const anchorDay = parseInt(payAnchorValue) || 1;
      if (now.getDate() >= anchorDay) {
        start = new Date(now.getFullYear(), now.getMonth(), anchorDay);
        end = new Date(now.getFullYear(), now.getMonth() + 1, anchorDay - 1);
      } else {
        start = new Date(now.getFullYear(), now.getMonth() - 1, anchorDay);
        end = new Date(now.getFullYear(), now.getMonth(), anchorDay - 1);
      }
    } else if (payAnchorType === "day") {
      // day of week (0=Sun, 1=Mon, ..., 5=Fri)
      const anchorDow = parseInt(payAnchorValue) || 5;
      const diff = (now.getDay() - anchorDow + 7) % 7;
      start = new Date(now);
      start.setDate(now.getDate() - diff);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else {
      // fallback to weekly
      start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    }
  } else {
    start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  }

  return { start, end };
}

function formatDateRange(start, end) {
  const opts = { month: "short", day: "numeric" };
  const startStr = start.toLocaleDateString("en-NZ", opts);
  const endStr = end.toLocaleDateString("en-NZ", { ...opts, year: "numeric" });
  return `${startStr} – ${endStr}`;
}

const RHYTHM_LABELS = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  per_pay_cycle: "Per pay cycle",
};

export function RhythmProvider({ children, householdId }) {
  const [rhythm, setRhythm] = useState({
    rhythm_type: "weekly",
    pay_anchor_type: null,
    pay_anchor_value: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchRhythm = useCallback(async () => {
    if (!householdId) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${householdId}/rhythm`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setRhythm(data);
      }
    } catch {
      // keep default
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => { fetchRhythm(); }, [fetchRhythm]);

  const updateRhythm = async (newRhythm) => {
    try {
      const res = await fetch(`${API_URL}/api/households/${householdId}/rhythm`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newRhythm),
      });
      if (res.ok) {
        const data = await res.json();
        setRhythm(data);
        return data;
      }
    } catch {
      // silent
    }
    return null;
  };

  const cycle = getCycleDates(rhythm.rhythm_type, rhythm.pay_anchor_type, rhythm.pay_anchor_value);
  const dateRange = formatDateRange(cycle.start, cycle.end);
  const label = RHYTHM_LABELS[rhythm.rhythm_type] || "Weekly";

  return (
    <RhythmContext.Provider value={{
      rhythm,
      loading,
      updateRhythm,
      cycleStart: cycle.start,
      cycleEnd: cycle.end,
      dateRange,
      label,
    }}>
      {children}
    </RhythmContext.Provider>
  );
}

export function useRhythm() {
  const ctx = useContext(RhythmContext);
  if (!ctx) return { rhythm: { rhythm_type: "weekly" }, loading: true, dateRange: "", label: "Weekly" };
  return ctx;
}
